import { inject, Injectable } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlag, NewPermission } from '@core/enums';
import { Store } from '@ngxs/store';
import { CustomObjectState } from '@shared/modules/custom-objects/state/state';
import { SavedViewAddComponent } from '@shared/modules/saved-views/modals/saved-view-add/saved-view-add.component';
import { AccountState } from '@state/account/state';
import { SavedViewsState } from '@state/saved-view/state';
import { UserState } from '@state/user/state';
import { TaskActions } from '@tasks/state/actions';
import { CreateTaskListComponent } from '@tasks/task-shell/create-task-list/create-task-list.component';
import { TaskRelatedToModule } from '@tasks/tasks.types';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { filter, take } from 'rxjs';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';

@Injectable()
export class TaskSidebarConfigService {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private matDialog = inject(MatLegacyDialog);
  private store = inject(Store);

  constructor() {}

  getConfig(relatedToType: TaskRelatedToModule): TsPageSidenavConfig {
    let actions = [];
    let canCreate = true;
    let canEdit = true;
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;
    const isGlobal = relatedToType === TaskRelatedToModule.Account;

    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS)) && !isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS)) && isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
    }

    if (canCreate) {
      actions.push({
        icon: 'add_circle_outline',
        description: 'Create new',
        actionFn: () => this.openCreateTaskListDialog(relatedToType),
      });
    }

    if (!isAggregatedTasks && canEdit) {
      actions.push({
        icon: 'settings',
        description: 'settings',
        actionFn: () => {
          this.router.navigate(['./settings'], { relativeTo: this.activatedRoute });
        },
      });
    }

    return {
      title: '',
      actions,
    };
  }

  openCreateTaskListDialog(relatedToType: TaskRelatedToModule) {
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;

    if (isAggregatedTasks) {
      const savedViewType = TaskRelatedToModule.AggregatedTasks;
      const dialogRef = this.matDialog.open(SavedViewAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          relatedToType: savedViewType,
          saved_views: this.store
            .selectSnapshot(SavedViewsState.getViews)
            .filter((x) => x.related_to_type === savedViewType),
        },
      });

      dialogRef
        .afterClosed()
        .pipe(
          filter((result) => !!result),
          take(1),
        )
        .subscribe(() => {
          this.store.dispatch(new TaskActions.GetSidebarItems());
        });
    } else {
      let applicableType = relatedToType as string;
      if (applicableType === TaskRelatedToModule.CustomObjectValue) {
        const selectedCo = this.store.selectSnapshot(CustomObjectState.getValueById);
        applicableType =
          selectedCo?.customObject?.name ?? this.store.selectSnapshot(JupiterState.getRelatedToObject).name;
      }

      this.matDialog.open(CreateTaskListComponent, { data: { applicableType } });
    }
  }
}
